import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  alpha,
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Radar as RadarIcon,
  ShowChart as ShowChartIcon,
  FilterNone as FunnelIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  DonutLarge as DonutIcon,
  BubbleChart as BubbleChartIcon,
  Analytics as AnalyticsIcon,
  AutoGraph as AutoGraphIcon,
  Insights as InsightsIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  RadialBarChart,
  RadialBar,
  FunnelChart,
  Funnel,
  LabelList,
  ComposedChart,
  Scatter,
  ScatterChart,
  ZAxis,
  Brush,
  ReferenceLine,
  ReferenceArea,
  ReferenceDot,
} from 'recharts';

// Enhanced color palette for presentation
const showcaseColors = {
  primary: '#0078D4',
  secondary: '#40E0D0',
  success: '#107C10',
  warning: '#FFB900',
  danger: '#D13438',
  purple: '#5C2D91',
  orange: '#FF8C00',
  pink: '#E91E63',
  teal: '#009688',
  indigo: '#3F51B5',
  gradient: {
    blue: ['#667eea', '#764ba2'],
    sunset: ['#f093fb', '#f5576c'],
    ocean: ['#2E3192', '#1BFFFF'],
    forest: ['#134E5E', '#71B280'],
  },
  chart: [
    '#0078D4', '#40E0D0', '#107C10', '#FFB900', '#5C2D91', '#D13438',
    '#FF8C00', '#E91E63', '#009688', '#3F51B5', '#00BCD4', '#8BC34A',
  ],
};

// Mock data generators
const generateTimeSeriesData = (days = 30) => {
  return Array.from({ length: days }, (_, i) => ({
    date: `Day ${i + 1}`,
    conversations: Math.floor(Math.random() * 100 + 50),
    answered: Math.floor(Math.random() * 70 + 40),
    unanswered: Math.floor(Math.random() * 30 + 10),
    messages: Math.floor(Math.random() * 300 + 150),
    responseTime: Math.floor(Math.random() * 20 + 5),
  }));
};

const channelData = [
  { name: 'WhatsApp', value: 580, color: showcaseColors.success },
  { name: 'Email', value: 220, color: showcaseColors.primary },
  { name: 'Web Chat', value: 120, color: showcaseColors.secondary },
  { name: 'SMS', value: 80, color: showcaseColors.warning },
];

const funnelData = [
  { stage: 'Leads Captured', value: 1800, fill: showcaseColors.chart[0] },
  { stage: 'Qualified', value: 1240, fill: showcaseColors.chart[1] },
  { stage: 'Engaged', value: 880, fill: showcaseColors.chart[2] },
  { stage: 'Agent Handoff', value: 510, fill: showcaseColors.chart[3] },
  { stage: 'Converted', value: 302, fill: showcaseColors.chart[4] },
];

const radarData = [
  { metric: 'Response Speed', value: 85, fullMark: 100 },
  { metric: 'CSAT', value: 78, fullMark: 100 },
  { metric: 'First Contact Resolution', value: 72, fullMark: 100 },
  { metric: 'Coverage', value: 90, fullMark: 100 },
  { metric: 'Automation Rate', value: 65, fullMark: 100 },
  { metric: 'Agent Efficiency', value: 82, fullMark: 100 },
];

const stackedBarData = [
  { channel: 'WhatsApp', human: 280, automation: 420 },
  { channel: 'Email', human: 90, automation: 160 },
  { channel: 'Web', human: 120, automation: 210 },
  { channel: 'SMS', human: 40, automation: 90 },
];

const radialBarData = [
  { name: 'Week 1', value: 62, fill: showcaseColors.chart[0] },
  { name: 'Week 2', value: 71, fill: showcaseColors.chart[1] },
  { name: 'Week 3', value: 78, fill: showcaseColors.chart[2] },
  { name: 'Week 4', value: 85, fill: showcaseColors.chart[3] },
];

const composedData = [
  { month: 'Jan', conversations: 420, avgResponseTime: 8.2, csat: 76 },
  { month: 'Feb', conversations: 480, avgResponseTime: 7.5, csat: 79 },
  { month: 'Mar', conversations: 520, avgResponseTime: 6.8, csat: 82 },
  { month: 'Apr', conversations: 610, avgResponseTime: 6.2, csat: 85 },
  { month: 'May', conversations: 680, avgResponseTime: 5.8, csat: 87 },
  { month: 'Jun', conversations: 720, avgResponseTime: 5.3, csat: 89 },
];

const scatterData = [
  { responseTime: 2.5, csat: 92, volume: 40 },
  { responseTime: 4.2, csat: 88, volume: 60 },
  { responseTime: 6.1, csat: 82, volume: 80 },
  { responseTime: 8.5, csat: 75, volume: 100 },
  { responseTime: 12.3, csat: 68, volume: 50 },
  { responseTime: 15.8, csat: 62, volume: 30 },
  { responseTime: 3.2, csat: 90, volume: 70 },
  { responseTime: 5.5, csat: 85, volume: 90 },
];

const multiAxisData = [
  { quarter: 'Q1', revenue: 42000, conversations: 1240, conversionRate: 24.5, teamSize: 8 },
  { quarter: 'Q2', revenue: 58000, conversations: 1680, conversionRate: 28.2, teamSize: 10 },
  { quarter: 'Q3', revenue: 67000, conversations: 1920, conversionRate: 31.8, teamSize: 12 },
  { quarter: 'Q4', revenue: 82000, conversations: 2340, conversionRate: 35.1, teamSize: 14 },
];

const hourlyPatternData = Array.from({ length: 24 }, (_, hour) => ({
  hour: `${hour}h`,
  weekday: Math.floor(40 + Math.sin((hour - 14) / 3) * 30 + Math.random() * 10),
  weekend: Math.floor(20 + Math.sin((hour - 12) / 4) * 15 + Math.random() * 8),
}));

const categoryComparisonData = [
  { category: 'Onboarding', current: 245, previous: 198, target: 280 },
  { category: 'Support', current: 520, previous: 480, target: 550 },
  { category: 'Sales', current: 182, previous: 156, target: 200 },
  { category: 'Retention', current: 93, previous: 88, target: 120 },
  { category: 'Feedback', current: 67, previous: 54, target: 80 },
];

const trendData = generateTimeSeriesData(30);

const pieVariantData = [
  { name: 'Automation resolved', value: 420, color: showcaseColors.success },
  { name: 'Agent resolved', value: 310, color: showcaseColors.primary },
  { name: 'Escalated', value: 85, color: showcaseColors.warning },
  { name: 'Abandoned', value: 45, color: showcaseColors.danger },
];

const nestedPieData = [
  { name: 'WhatsApp', value: 580, category: 'Messaging' },
  { name: 'Telegram', value: 120, category: 'Messaging' },
  { name: 'Email', value: 220, category: 'Email' },
  { name: 'Web Chat', value: 180, category: 'Web' },
  { name: 'Widget', value: 90, category: 'Web' },
];

const areaVariations = generateTimeSeriesData(20).map((d, i) => ({
  ...d,
  automation: Math.floor(Math.random() * 50 + 30),
  human: Math.floor(Math.random() * 40 + 20),
  aiSuggested: Math.floor(Math.random() * 30 + 10),
}));

const ChartCard = ({ title, subtitle, children, icon: Icon, gradientColors }) => {
  const gradientId = `gradient-${title.replace(/\s/g, '-')}`;
  
  return (
    <Paper
      sx={{
        p: 3,
        height: '100%',
        background: gradientColors 
          ? `linear-gradient(135deg, ${alpha(gradientColors[0], 0.05)} 0%, ${alpha(gradientColors[1], 0.05)} 100%)`
          : '#FFFFFF',
        borderRadius: 3,
        border: `1px solid ${alpha(showcaseColors.primary, 0.12)}`,
        boxShadow: '0 8px 32px rgba(0, 120, 212, 0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: gradientColors
            ? `linear-gradient(90deg, ${gradientColors[0]}, ${gradientColors[1]})`
            : `linear-gradient(90deg, ${showcaseColors.primary}, ${showcaseColors.secondary})`,
        },
        '&:hover': {
          boxShadow: '0 16px 48px rgba(0, 120, 212, 0.12)',
          transform: 'translateY(-4px)',
        },
      }}
    >
      <svg width="0" height="0">
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={gradientColors?.[0] || showcaseColors.primary} stopOpacity={0.8} />
            <stop offset="100%" stopColor={gradientColors?.[1] || showcaseColors.secondary} stopOpacity={0.3} />
          </linearGradient>
        </defs>
      </svg>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        {Icon && (
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              background: gradientColors
                ? `linear-gradient(135deg, ${alpha(gradientColors[0], 0.15)}, ${alpha(gradientColors[1], 0.15)})`
                : `linear-gradient(135deg, ${alpha(showcaseColors.primary, 0.15)}, ${alpha(showcaseColors.secondary, 0.15)})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ fontSize: 24, color: gradientColors?.[0] || showcaseColors.primary }} />
          </Box>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {children}
    </Paper>
  );
};

const ChartsShowcase = () => {
  const timeSeriesData = generateTimeSeriesData(14);

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      p: { xs: 2, md: 4 }
    }}>
      {/* Hero Header */}
      <Box sx={{ mb: 5, textAlign: 'center' }}>
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: 800, 
            mb: 2,
            background: `linear-gradient(135deg, ${showcaseColors.primary} 0%, ${showcaseColors.purple} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Charts & Visualizations Showcase
        </Typography>
        <Typography variant="h6" sx={{ color: '#666', fontWeight: 400, mb: 3 }}>
          Comprehensive demonstration of all available chart types and data visualizations
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Chip label="Line Charts" color="primary" />
          <Chip label="Area Charts" sx={{ bgcolor: showcaseColors.secondary, color: 'white' }} />
          <Chip label="Bar Charts" sx={{ bgcolor: showcaseColors.success, color: 'white' }} />
          <Chip label="Pie Charts" sx={{ bgcolor: showcaseColors.warning, color: 'white' }} />
          <Chip label="Funnel" sx={{ bgcolor: showcaseColors.purple, color: 'white' }} />
          <Chip label="Radar" sx={{ bgcolor: showcaseColors.danger, color: 'white' }} />
          <Chip label="Radial" sx={{ bgcolor: showcaseColors.orange, color: 'white' }} />
          <Chip label="Composed" sx={{ bgcolor: showcaseColors.pink, color: 'white' }} />
        </Box>
      </Box>

      {/* Section 1: Line & Area Charts */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Time Series & Trends
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Multi-Line Chart"
            subtitle="Conversation trends over 2 weeks"
            icon={TimelineIcon}
            gradientColors={showcaseColors.gradient.blue}
          >
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="conversations" stroke={showcaseColors.primary} strokeWidth={3} dot={{ r: 4 }} name="Total Conversations" />
                <Line type="monotone" dataKey="answered" stroke={showcaseColors.success} strokeWidth={2} dot={{ r: 3 }} name="Answered" />
                <Line type="monotone" dataKey="unanswered" stroke={showcaseColors.danger} strokeWidth={2} strokeDasharray="5 5" name="Unanswered" />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Stacked Area Chart"
            subtitle="Message volume distribution"
            icon={ShowChartIcon}
            gradientColors={showcaseColors.gradient.ocean}
          >
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={timeSeriesData}>
                <defs>
                  <linearGradient id="colorAnswered" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={showcaseColors.primary} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={showcaseColors.primary} stopOpacity={0.1}/>
                  </linearGradient>
                  <linearGradient id="colorUnanswered" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={showcaseColors.danger} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={showcaseColors.danger} stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="answered" 
                  stackId="1"
                  stroke={showcaseColors.primary} 
                  fillOpacity={1} 
                  fill="url(#colorAnswered)" 
                  name="Answered Sessions"
                />
                <Area 
                  type="monotone" 
                  dataKey="unanswered" 
                  stackId="1"
                  stroke={showcaseColors.danger} 
                  fillOpacity={1} 
                  fill="url(#colorUnanswered)" 
                  name="Unanswered Sessions"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 2: Bar Charts */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Comparative & Distribution Analysis
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Stacked Bar Chart"
            subtitle="Human vs Automation breakdown by channel"
            icon={BarChartIcon}
            gradientColors={showcaseColors.gradient.forest}
          >
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={stackedBarData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="channel" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="human" stackId="a" fill={showcaseColors.primary} name="Human Agents" radius={[0, 0, 0, 0]} />
                <Bar dataKey="automation" stackId="a" fill={showcaseColors.secondary} name="Automation" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Grouped Bar Chart"
            subtitle="Monthly performance comparison"
            icon={BarChartIcon}
            gradientColors={showcaseColors.gradient.sunset}
          >
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={composedData.slice(0, 4)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="conversations" fill={showcaseColors.primary} name="Conversations" radius={[6, 6, 0, 0]} />
                <Bar dataKey="csat" fill={showcaseColors.success} name="CSAT Score" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 3: Circular Charts */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Proportions & Composition
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={4}>
          <ChartCard
            title="Donut Chart"
            subtitle="Channel distribution"
            icon={DonutIcon}
            gradientColors={[showcaseColors.purple, showcaseColors.pink]}
          >
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={channelData}
                  cx="50%"
                  cy="50%"
                  innerRadius={80}
                  outerRadius={130}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {channelData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <ChartCard
            title="Funnel Chart"
            subtitle="Lead to conversion pipeline"
            icon={FunnelIcon}
            gradientColors={[showcaseColors.orange, showcaseColors.danger]}
          >
            <ResponsiveContainer width="100%" height={350}>
              <FunnelChart>
                <Tooltip />
                <Funnel 
                  dataKey="value" 
                  data={funnelData} 
                  isAnimationActive
                >
                  <LabelList position="right" fill="#000" stroke="none" dataKey="stage" />
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <ChartCard
            title="Radial Bar Chart"
            subtitle="Weekly goal achievement"
            icon={SpeedIcon}
            gradientColors={[showcaseColors.teal, showcaseColors.success]}
          >
            <ResponsiveContainer width="100%" height={350}>
              <RadialBarChart 
                innerRadius="10%" 
                outerRadius="90%" 
                data={radialBarData} 
                startAngle={180} 
                endAngle={0}
              >
                <RadialBar minAngle={15} background clockWise dataKey="value" />
                <Legend 
                  iconSize={10} 
                  layout="vertical" 
                  verticalAlign="middle" 
                  align="right"
                />
                <Tooltip />
              </RadialBarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 4: Advanced Charts */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Advanced Visualizations
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Radar Chart"
            subtitle="Multi-dimensional performance metrics"
            icon={RadarIcon}
            gradientColors={[showcaseColors.indigo, showcaseColors.purple]}
          >
            <ResponsiveContainer width="100%" height={400}>
              <RadarChart data={radarData}>
                <PolarGrid stroke={alpha(showcaseColors.primary, 0.3)} />
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11, fill: '#666' }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Radar 
                  name="Performance Score" 
                  dataKey="value" 
                  stroke={showcaseColors.primary} 
                  fill={showcaseColors.primary} 
                  fillOpacity={0.5}
                  strokeWidth={2}
                />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Composed Chart (Bar + Line)"
            subtitle="Volume vs Quality metrics over time"
            icon={TrendingUpIcon}
            gradientColors={[showcaseColors.success, showcaseColors.warning]}
          >
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={composedData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 12 }} label={{ value: 'Conversations', angle: -90, position: 'insideLeft' }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} label={{ value: 'Response Time (min)', angle: 90, position: 'insideRight' }} />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="conversations" fill={showcaseColors.primary} name="Conversations" radius={[8, 8, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="avgResponseTime" stroke={showcaseColors.danger} strokeWidth={3} dot={{ r: 5 }} name="Avg Response Time" />
                <Line yAxisId="right" type="monotone" dataKey="csat" stroke={showcaseColors.success} strokeWidth={3} dot={{ r: 5 }} strokeDasharray="5 5" name="CSAT" />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 5: Scatter & Correlation */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Correlation & Patterns
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Scatter Plot"
            subtitle="Response time vs CSAT correlation"
            icon={ShowChartIcon}
            gradientColors={[showcaseColors.pink, showcaseColors.orange]}
          >
            <ResponsiveContainer width="100%" height={400}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis 
                  type="number" 
                  dataKey="responseTime" 
                  name="Response Time" 
                  unit=" min"
                  tick={{ fontSize: 12 }}
                  label={{ value: 'Response Time (minutes)', position: 'insideBottom', offset: -5 }}
                />
                <YAxis 
                  type="number" 
                  dataKey="csat" 
                  name="CSAT" 
                  unit="%"
                  tick={{ fontSize: 12 }}
                  label={{ value: 'CSAT Score', angle: -90, position: 'insideLeft' }}
                />
                <ZAxis type="number" dataKey="volume" range={[50, 400]} name="Volume" />
                <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                <Scatter name="Conversations" data={scatterData} fill={showcaseColors.primary}>
                  {scatterData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={showcaseColors.chart[index % showcaseColors.chart.length]} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Percentage Area Chart"
            subtitle="Response type distribution over time (normalized to 100%)"
            icon={PieChartIcon}
            gradientColors={[showcaseColors.teal, showcaseColors.indigo]}
          >
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart data={timeSeriesData.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `${value}%`} />
                <Tooltip formatter={(value, name) => [`${value.toFixed(1)}%`, name]} />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="answered" 
                  stackId="1"
                  stroke={showcaseColors.success} 
                  fill={showcaseColors.success}
                  fillOpacity={0.6}
                  name="Answered %"
                />
                <Area 
                  type="monotone" 
                  dataKey="unanswered" 
                  stackId="1"
                  stroke={showcaseColors.warning} 
                  fill={showcaseColors.warning}
                  fillOpacity={0.6}
                  name="Unanswered %"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 6: Horizontal & Comparative */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Rankings & Comparisons
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ChartCard
            title="Horizontal Bar Chart"
            subtitle="Top performing channels with dual metrics"
            icon={BarChartIcon}
            gradientColors={showcaseColors.gradient.blue}
          >
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stackedBarData} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="channel" tick={{ fontSize: 12 }} width={100} />
                <Tooltip />
                <Legend />
                <Bar dataKey="human" fill={showcaseColors.primary} name="Human Touch" radius={[0, 8, 8, 0]} />
                <Bar dataKey="automation" fill={alpha(showcaseColors.secondary, 0.8)} name="Automation" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 7: Advanced Variations */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Advanced Variations & Multi-Axis
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Triple-Axis Composed Chart"
            subtitle="Revenue, Conversations & Conversion on different scales"
            icon={AutoGraphIcon}
            gradientColors={[showcaseColors.indigo, showcaseColors.teal]}
          >
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={multiAxisData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="quarter" tick={{ fontSize: 12 }} />
                <YAxis 
                  yAxisId="left" 
                  tick={{ fontSize: 11 }} 
                  label={{ value: 'Revenue ($)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
                  stroke={showcaseColors.success}
                />
                <YAxis 
                  yAxisId="middle" 
                  orientation="right"
                  tick={{ fontSize: 11 }} 
                  label={{ value: 'Conversations', angle: 90, position: 'insideRight', style: { fontSize: 11 } }}
                  stroke={showcaseColors.primary}
                />
                <YAxis 
                  yAxisId="right" 
                  orientation="right"
                  tick={{ fontSize: 11 }} 
                  label={{ value: 'Conversion %', angle: 90, position: 'outsideRight', style: { fontSize: 11 } }}
                  stroke={showcaseColors.warning}
                />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="revenue" fill={alpha(showcaseColors.success, 0.7)} name="Revenue" radius={[8, 8, 0, 0]} />
                <Line yAxisId="middle" type="monotone" dataKey="conversations" stroke={showcaseColors.primary} strokeWidth={3} dot={{ r: 5 }} name="Conversations" />
                <Line yAxisId="right" type="monotone" dataKey="conversionRate" stroke={showcaseColors.warning} strokeWidth={2} strokeDasharray="4 4" dot={{ r: 4 }} name="Conversion %" />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="24h Activity Pattern"
            subtitle="Weekday vs Weekend comparison"
            icon={SpeedIcon}
            gradientColors={showcaseColors.gradient.ocean}
          >
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart data={hourlyPatternData}>
                <defs>
                  <linearGradient id="weekdayGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={showcaseColors.primary} stopOpacity={0.9}/>
                    <stop offset="95%" stopColor={showcaseColors.primary} stopOpacity={0.1}/>
                  </linearGradient>
                  <linearGradient id="weekendGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={showcaseColors.secondary} stopOpacity={0.9}/>
                    <stop offset="95%" stopColor={showcaseColors.secondary} stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={1} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="weekday" 
                  stroke={showcaseColors.primary}
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#weekdayGrad)" 
                  name="Weekday Activity"
                />
                <Area 
                  type="monotone" 
                  dataKey="weekend" 
                  stroke={showcaseColors.secondary}
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#weekendGrad)" 
                  name="Weekend Activity"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 8: Multiple Pie Variants */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Pie Chart Variations
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={4}>
          <ChartCard
            title="Standard Pie Chart"
            subtitle="Resolution status breakdown"
            icon={PieChartIcon}
            gradientColors={[showcaseColors.success, showcaseColors.warning]}
          >
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={pieVariantData}
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  dataKey="value"
                  label={({ name, percent }) => `${(percent * 100).toFixed(1)}%`}
                  labelLine={{ stroke: '#999', strokeWidth: 1 }}
                >
                  {pieVariantData.map((entry, index) => (
                    <Cell key={`pie-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <ChartCard
            title="Semi-Circle Gauge"
            subtitle="Current period performance vs target"
            icon={SpeedIcon}
            gradientColors={[showcaseColors.primary, showcaseColors.purple]}
          >
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Achieved', value: 78, fill: showcaseColors.success },
                    { name: 'Remaining', value: 22, fill: alpha(showcaseColors.warning, 0.3) },
                  ]}
                  cx="50%"
                  cy="70%"
                  startAngle={180}
                  endAngle={0}
                  innerRadius={80}
                  outerRadius={130}
                  dataKey="value"
                >
                  <LabelList position="center" content={({ viewBox }) => {
                    const { cx, cy } = viewBox;
                    return (
                      <text x={cx} y={cy - 10} textAnchor="middle" dominantBaseline="middle">
                        <tspan x={cx} fontSize="48" fontWeight="700" fill={showcaseColors.success}>78%</tspan>
                        <tspan x={cx} dy="30" fontSize="14" fill="#666">Target Achievement</tspan>
                      </text>
                    );
                  }} />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <ChartCard
            title="Multi-Level Donut"
            subtitle="Hierarchical channel breakdown"
            icon={DonutIcon}
            gradientColors={[showcaseColors.orange, showcaseColors.pink]}
          >
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={channelData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  dataKey="value"
                  paddingAngle={3}
                >
                  {channelData.map((entry, index) => (
                    <Cell key={`outer-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Pie
                  data={pieVariantData}
                  cx="50%"
                  cy="50%"
                  innerRadius={90}
                  outerRadius={130}
                  dataKey="value"
                  paddingAngle={2}
                  label
                >
                  {pieVariantData.map((entry, index) => (
                    <Cell key={`inner-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 9: Area Chart Variations */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Area Chart Variations
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Stream Graph (Stacked Smooth)"
            subtitle="Team contribution over time"
            icon={ShowChartIcon}
            gradientColors={showcaseColors.gradient.forest}
          >
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={areaVariations}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Area 
                  type="basis" 
                  dataKey="automation" 
                  stackId="1"
                  stroke={showcaseColors.secondary} 
                  fill={showcaseColors.secondary}
                  fillOpacity={0.7}
                  name="Automation"
                />
                <Area 
                  type="basis" 
                  dataKey="human" 
                  stackId="1"
                  stroke={showcaseColors.primary} 
                  fill={showcaseColors.primary}
                  fillOpacity={0.7}
                  name="Human Agents"
                />
                <Area 
                  type="basis" 
                  dataKey="aiSuggested" 
                  stackId="1"
                  stroke={showcaseColors.purple} 
                  fill={showcaseColors.purple}
                  fillOpacity={0.7}
                  name="AI-Suggested"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Stepped Area Chart"
            subtitle="Response time buckets distribution"
            icon={TimelineIcon}
            gradientColors={showcaseColors.gradient.sunset}
          >
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={trendData.slice(0, 15)}>
                <defs>
                  <linearGradient id="steppedGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={showcaseColors.warning} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={showcaseColors.danger} stopOpacity={0.2}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Area 
                  type="step" 
                  dataKey="responseTime" 
                  stroke={showcaseColors.danger}
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#steppedGrad)" 
                  name="Response Time (min)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 10: Category Comparisons */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Performance Comparisons
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12}>
          <ChartCard
            title="Current vs Previous vs Target"
            subtitle="Category performance benchmarking"
            icon={InsightsIcon}
            gradientColors={[showcaseColors.primary, showcaseColors.success]}
          >
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={categoryComparisonData} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="category" tick={{ fontSize: 12 }} width={100} />
                <Tooltip />
                <Legend />
                <Bar dataKey="previous" fill={alpha(showcaseColors.chart[5], 0.4)} name="Previous Period" radius={[0, 4, 4, 0]} />
                <Bar dataKey="current" fill={showcaseColors.primary} name="Current Period" radius={[0, 6, 6, 0]} />
                <Bar dataKey="target" fill={showcaseColors.success} name="Target" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 11: Bubble & 3D Representations */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Multi-Dimensional Data
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Bubble Chart (3 Variables)"
            subtitle="Response Time × CSAT × Volume"
            icon={BubbleChartIcon}
            gradientColors={[showcaseColors.pink, showcaseColors.purple]}
          >
            <ResponsiveContainer width="100%" height={400}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis 
                  type="number" 
                  dataKey="responseTime" 
                  name="Response Time" 
                  unit=" min"
                  tick={{ fontSize: 12 }}
                  label={{ value: 'Response Time (minutes)', position: 'insideBottom', offset: -5 }}
                  domain={[0, 20]}
                />
                <YAxis 
                  type="number" 
                  dataKey="csat" 
                  name="CSAT" 
                  unit="%"
                  tick={{ fontSize: 12 }}
                  label={{ value: 'Customer Satisfaction (%)', angle: -90, position: 'insideLeft' }}
                  domain={[50, 100]}
                />
                <ZAxis type="number" dataKey="volume" range={[100, 800]} name="Conversation Volume" />
                <Tooltip 
                  cursor={{ strokeDasharray: '3 3' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <Box sx={{ bgcolor: 'white', p: 1.5, borderRadius: 1, boxShadow: 2, border: `1px solid ${alpha('#000', 0.1)}` }}>
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                            Response: {data.responseTime} min
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            CSAT: {data.csat}%
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            Volume: {data.volume} conv.
                          </Typography>
                        </Box>
                      );
                    }
                    return null;
                  }}
                />
                <Scatter name="Agent Performance" data={scatterData} fill={showcaseColors.primary} fillOpacity={0.6} />
              </ScatterChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Normalized Trend Lines"
            subtitle="Comparative growth rates (base 100)"
            icon={TrendingUpIcon}
            gradientColors={showcaseColors.gradient.blue}
          >
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={trendData.slice(0, 20).map((d, i) => ({
                date: d.date,
                conversations: 100 + i * 3.2 + Math.random() * 8,
                automation: 100 + i * 4.5 + Math.random() * 10,
                csat: 100 + i * 1.8 + Math.random() * 5,
              }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  domain={[90, 'auto']}
                  label={{ value: 'Index (base 100)', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip formatter={(value) => value.toFixed(1)} />
                <Legend />
                <Line type="monotone" dataKey="conversations" stroke={showcaseColors.primary} strokeWidth={3} dot={false} name="Conversations Growth" />
                <Line type="monotone" dataKey="automation" stroke={showcaseColors.secondary} strokeWidth={3} dot={false} name="Automation Adoption" />
                <Line type="monotone" dataKey="csat" stroke={showcaseColors.success} strokeWidth={3} dot={false} strokeDasharray="5 5" name="CSAT Trend" />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 12: Dual Funnels Comparison */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Funnel Analysis
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Acquisition Funnel"
            subtitle="Lead to customer journey with conversion rates"
            icon={FunnelIcon}
            gradientColors={[showcaseColors.orange, showcaseColors.danger]}
          >
            <ResponsiveContainer width="100%" height={450}>
              <FunnelChart>
                <Tooltip 
                  content={({ payload }) => {
                    if (payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <Box sx={{ bgcolor: 'white', p: 1.5, borderRadius: 1, boxShadow: 2 }}>
                          <Typography variant="body2" fontWeight={600}>{data.stage}</Typography>
                          <Typography variant="caption" display="block">Volume: {data.value}</Typography>
                          <Typography variant="caption" display="block" color={showcaseColors.success}>
                            Conversion: {data.conversion}
                          </Typography>
                        </Box>
                      );
                    }
                    return null;
                  }}
                />
                <Funnel dataKey="value" data={funnelData} isAnimationActive>
                  <LabelList position="right" fill="#333" stroke="none" dataKey="stage" style={{ fontSize: 13 }} />
                  <LabelList position="inside" fill="white" stroke="none" dataKey="conversion" style={{ fontSize: 14, fontWeight: 600 }} />
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Inverted Funnel (Expansion)"
            subtitle="Customer lifecycle value expansion"
            icon={FunnelIcon}
            gradientColors={[showcaseColors.success, showcaseColors.teal]}
          >
            <ResponsiveContainer width="100%" height={450}>
              <FunnelChart>
                <Tooltip />
                <Funnel 
                  dataKey="value" 
                  data={[
                    { stage: 'New Customer', value: 300, fill: showcaseColors.chart[0] },
                    { stage: 'Repeat Purchase', value: 480, fill: showcaseColors.chart[1] },
                    { stage: 'Premium Tier', value: 620, fill: showcaseColors.chart[2] },
                    { stage: 'Brand Advocate', value: 850, fill: showcaseColors.chart[3] },
                    { stage: 'Enterprise', value: 1200, fill: showcaseColors.chart[4] },
                  ].reverse()}
                  isAnimationActive
                >
                  <LabelList position="right" fill="#333" stroke="none" dataKey="stage" style={{ fontSize: 13 }} />
                  <LabelList position="inside" fill="white" stroke="none" dataKey="value" style={{ fontSize: 14, fontWeight: 600 }} />
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 13: Specialized Comparisons */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Benchmarking & KPI Tracking
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Multi-Metric Radar"
            subtitle="Performance scorecard across 6 dimensions"
            icon={RadarIcon}
            gradientColors={[showcaseColors.indigo, showcaseColors.purple]}
          >
            <ResponsiveContainer width="100%" height={400}>
              <RadarChart data={radarData}>
                <PolarGrid stroke={alpha(showcaseColors.primary, 0.2)} />
                <PolarAngleAxis 
                  dataKey="metric" 
                  tick={{ fontSize: 11, fill: '#444' }}
                  tickLine={false}
                />
                <PolarRadiusAxis 
                  angle={90} 
                  domain={[0, 100]} 
                  tick={{ fontSize: 10, fill: '#666' }}
                  tickCount={6}
                />
                <Radar 
                  name="Current Quarter" 
                  dataKey="value" 
                  stroke={showcaseColors.primary} 
                  fill={showcaseColors.primary} 
                  fillOpacity={0.5}
                  strokeWidth={2}
                  dot={{ r: 4, fill: showcaseColors.primary }}
                />
                <Tooltip />
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Radial Progress Tracker"
            subtitle="Weekly goals completion (circular view)"
            icon={SpeedIcon}
            gradientColors={[showcaseColors.teal, showcaseColors.success]}
          >
            <ResponsiveContainer width="100%" height={400}>
              <RadialBarChart 
                innerRadius="20%" 
                outerRadius="100%" 
                data={radialBarData} 
                startAngle={90} 
                endAngle={-270}
              >
                <RadialBar 
                  minAngle={15} 
                  background 
                  clockWise 
                  dataKey="value"
                  label={{ position: 'insideStart', fill: '#fff', fontSize: 12, fontWeight: 600 }}
                  cornerRadius={8}
                />
                <Legend 
                  iconSize={10} 
                  layout="vertical" 
                  verticalAlign="middle" 
                  align="right"
                  wrapperStyle={{ paddingLeft: 20 }}
                />
                <Tooltip 
                  formatter={(value) => `${value}%`}
                  contentStyle={{ borderRadius: 8, border: `1px solid ${alpha('#000', 0.1)}` }}
                />
              </RadialBarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Footer info */}
      <Box sx={{ mt: 6, p: 4, textAlign: 'center', bgcolor: alpha('#fff', 0.9), borderRadius: 3, border: `1px solid ${alpha(showcaseColors.primary, 0.1)}` }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: showcaseColors.primary }}>
          📊 Recharts Library
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          This showcase demonstrates the full range of visualization capabilities using Recharts.
          All data shown is synthetic and generated for demonstration purposes only.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mt: 2 }}>
          <Chip label="13 Chart Types" size="small" color="primary" />
          <Chip label="Multiple Variations" size="small" sx={{ bgcolor: showcaseColors.secondary, color: 'white' }} />
          <Chip label="Professional Styling" size="small" sx={{ bgcolor: showcaseColors.success, color: 'white' }} />
          <Chip label="Fully Responsive" size="small" sx={{ bgcolor: showcaseColors.purple, color: 'white' }} />
        </Box>
      </Box>
    </Box>
  );
};

export default ChartsShowcase;

